/**
 * Place a ban on an address or a range.
 *
 * The rule is editable even when it arrives prefilled from a user's address list, because the
 * useful ban is often wider than the one address you clicked — the /24 around it, say — and
 * narrowing or widening it belongs in the same place as the reason for it.
 *
 * The expiry is a small set of durations rather than a date picker. A ban with no end is a
 * decision someone should make on purpose, so it is the last option and labelled as such.
 */

import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { ipApi } from '@api/endpoints/ip'
import type { IpBanDto } from '@api/types/ip'
import { IP_BANS_KEY } from '../users/userQueryKeys'
import { errorMessage } from '../users/userFormatting'
import { describeBroadRange } from './ipFormatting'

const DURATIONS: { label: string; hours: number | null }[] = [
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 24 * 7 },
  { label: '30 days', hours: 24 * 30 },
  { label: '90 days', hours: 24 * 90 },
  { label: 'Until lifted', hours: null },
]

export interface IpBanDialogProps {
  open: boolean
  onClose: () => void
  /** Prefill the rule, e.g. from an address on a user's page. */
  initialRule?: string
  /** The account the ban is being placed from, for the record. */
  subjectUserId?: string
  onBanned?: (ban: IpBanDto) => void
}

export function IpBanDialog({ open, onClose, initialRule = '', subjectUserId, onBanned }: IpBanDialogProps) {
  const mobile = useMobileDialogProps()
  const queryClient = useQueryClient()
  const [rule, setRule] = useState(initialRule)
  const [reason, setReason] = useState('')
  const [durationIndex, setDurationIndex] = useState(2)
  const [confirmBroad, setConfirmBroad] = useState(false)

  // Re-seed each time the dialog opens for a different address; a stale rule from the last
  // one is the wrong thing to ban.
  useEffect(() => {
    if (open) {
      setRule(initialRule)
      setReason('')
      setDurationIndex(2)
      setConfirmBroad(false)
    }
  }, [open, initialRule])

  const create = useMutation({
    mutationFn: () => {
      const hours = DURATIONS[durationIndex]?.hours ?? null
      return ipApi.createBan({
        rule: rule.trim(),
        reason: reason.trim(),
        expiresAt: hours == null ? undefined : new Date(Date.now() + hours * 3600_000).toISOString(),
        subjectUserId,
      })
    },
    onSuccess: (ban) => {
      queryClient.invalidateQueries({ queryKey: IP_BANS_KEY })
      // The lookup panel on /ip-bans shows "Ban this address" until it hears otherwise.
      queryClient.invalidateQueries({ queryKey: ['ip-lookup'] })
      setConfirmBroad(false)
      onBanned?.(ban)
      onClose()
    },
  })

  const canSubmit = rule.trim().length > 0 && reason.trim().length > 0 && !create.isPending
  // A /8 typed by mistake for a /28 locks out a whole carrier's customers, so wide ranges get a
  // warning while typing and a second confirmation on submit.
  const broad = describeBroadRange(rule)
  const submit = () => (broad ? setConfirmBroad(true) : create.mutate())

  return (
    <Dialog open={open} onClose={create.isPending ? undefined : onClose} maxWidth="sm" fullWidth {...mobile}>
      <DialogTitle>Ban an address</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Every request to the app from a matching address is refused, signed in or not. The
            panel itself is never blocked. Takes effect within about thirty seconds.
          </Typography>
          <TextField
            label="Address or range"
            value={rule}
            onChange={(e) => setRule(e.target.value)}
            placeholder="203.0.113.9 or 203.0.113.0/24"
            helperText="A single IP, or a CIDR range. Phones on carrier networks share addresses with many strangers — a range catches far more bystanders than one address."
            fullWidth
            autoFocus
            inputProps={{ spellCheck: false, autoCapitalize: 'off' }}
          />
          {broad && (
            <Alert severity="warning">
              This is a very broad range: {broad}. It will block large numbers of people who have
              nothing to do with this. Double-check the prefix.
            </Alert>
          )}
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            required
          />
          <TextField
            select
            label="Duration"
            value={durationIndex}
            onChange={(e) => setDurationIndex(Number(e.target.value))}
            fullWidth
          >
            {DURATIONS.map((d, i) => (
              <MenuItem key={d.label} value={i}>
                {d.label}
              </MenuItem>
            ))}
          </TextField>
          {create.error && <Alert severity="error">{errorMessage(create.error, 'Failed to place the ban')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={create.isPending}>
          Cancel
        </Button>
        <Button onClick={submit} color="error" variant="contained" disabled={!canSubmit}>
          {create.isPending ? 'Banning…' : 'Ban'}
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={confirmBroad}
        title="Ban this whole range?"
        message={`${rule.trim()}: ${broad ?? ''}. Everyone on it will be refused by the app within about thirty seconds, including people who have done nothing wrong.`}
        confirmLabel="Ban the range"
        destructive
        loading={create.isPending}
        onConfirm={() => create.mutate()}
        onClose={() => setConfirmBroad(false)}
      />
    </Dialog>
  )
}
